import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';

const TYPE_META = {
  order_pending: { icon: 'bi-cart-check', tone: 'warning' },
  low_stock: { icon: 'bi-box-seam', tone: 'warning' },
  out_of_stock: { icon: 'bi-exclamation-triangle', tone: 'danger' },
  abandoned_carts: { icon: 'bi-cart-x', tone: 'info' },
};

function toneClass(tone) {
  if (tone === 'danger') return 'adm-notif-item--danger';
  if (tone === 'warning') return 'adm-notif-item--warning';
  return 'adm-notif-item--info';
}

export default function AdminNotifications() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const {
    notifications,
    summary,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
  } = useAdminNotifications({ enabled: true });

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!prev) refresh({ silent: true });
      return next;
    });
  };

  const handleOpenItem = (item) => {
    markRead(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <div className="adm-notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`adm-topbar-icon adm-notif-btn${open ? ' is-open' : ''}`}
        aria-label="Notificaciones"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <i className="bi bi-bell" />
        {unreadCount > 0 && (
          <span className="adm-notif-badge" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="adm-notif-panel" role="dialog" aria-label="Notificaciones del panel">
          <div className="adm-notif-panel__head">
            <div>
              <strong>Notificaciones</strong>
              {summary && (
                <span className="adm-notif-panel__sub">
                  {summary.pendingOrders || 0} pedidos · {summary.lowStock || 0} stock bajo
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button type="button" className="adm-notif-panel__mark" onClick={markAllRead}>
                Marcar leídas
              </button>
            )}
          </div>

          <div className="adm-notif-panel__body">
            {loading && notifications.length === 0 ? (
              <div className="adm-notif-empty">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                <span>Cargando avisos…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="adm-notif-empty">
                <i className="bi bi-check2-circle" aria-hidden="true" />
                <span>Todo al día. No hay alertas.</span>
              </div>
            ) : (
              notifications.map((item) => {
                const meta = TYPE_META[item.type] || TYPE_META.abandoned_carts;
                const tone = item.severity || meta.tone;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`adm-notif-item ${toneClass(tone)}${item.unread ? ' is-unread' : ''}`}
                    onClick={() => handleOpenItem(item)}
                  >
                    <span className={`adm-notif-item__icon adm-notif-item__icon--${tone}`}>
                      <i className={`bi ${meta.icon}`} aria-hidden="true" />
                    </span>
                    <span className="adm-notif-item__content">
                      <span className="adm-notif-item__title">{item.title}</span>
                      <span className="adm-notif-item__msg">{item.message}</span>
                    </span>
                    {item.when && <span className="adm-notif-item__when">{item.when}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
